import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiBriefcase,
  FiCheckCircle,
  FiDollarSign,
  FiEdit2,
  FiEye,
  FiGrid,
  FiShield,
  FiTrash2,
  FiUsers,
} from 'react-icons/fi';
import { graphqlRequest } from '../utils/graphqlRequest';
import { readStoredUserRoles, isSuperAdminUser } from '../utils/adminProfileRouting';
import '../styles/BranchManagement.css';

const GET_BRANCHES = `
  query GetMyCompanyBranches {
    getMyCompanyBranches {
      id
      name
      location
      managerName
      managerEmail
      managerPhone
      startDate
      isActive
      adminUserId
      postcode
      latitude
      longitude
      createdAt
      totalRevenue
      activeProjects
      workforceCount
      openDeals
      slaCompliance
      qualityScore
      slaStatus
    }
  }
`;

const CREATE_BRANCH = `
  mutation CreateBranch($input: CreateBranchInput!) {
    createBranch(input: $input) {
      id
      name
      location
      managerName
      managerEmail
      managerPhone
      startDate
      isActive
      adminUserId
      createdAt
      totalRevenue
      activeProjects
      workforceCount
      openDeals
      slaCompliance
      qualityScore
      slaStatus
    }
  }
`;

const UPDATE_BRANCH_LOCATION = `
  mutation UpdateBranchLocation($input: UpdateBranchLocationInput!) {
    updateBranchLocation(input: $input) {
      id
      postcode
      latitude
      longitude
      updatedAt
    }
  }
`;

const UPDATE_BRANCH = `
  mutation UpdateBranch($input: UpdateBranchInput!) {
    updateBranch(input: $input) {
      id
      name
      location
      managerName
      managerEmail
      managerPhone
      startDate
      isActive
      adminUserId
      postcode
      latitude
      longitude
      createdAt
      totalRevenue
      activeProjects
      workforceCount
      openDeals
      slaCompliance
      qualityScore
      slaStatus
    }
  }
`;

const DELETE_BRANCH = `
  mutation DeleteBranch($branchId: String!) {
    deleteBranch(branchId: $branchId)
  }
`;

const GET_ALL_USERS = `
  query GetAllUsersForBranchAdminPicker {
    getAllUser(page: 1, limit: 100) {
      data {
        id
        firstName
        lastName
        email
        roles
        isActive
      }
    }
  }
`;

const roleKey = (value) =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '');

export default function BranchManagement() {
  const navigate = useNavigate();
  const roles = useMemo(() => readStoredUserRoles(), []);
  const isSuperAdmin = isSuperAdminUser(roles);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [viewBranch, setViewBranch] = useState(null);
  const [editBranch, setEditBranch] = useState(null);
  const [geoBranch, setGeoBranch] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [managerName, setManagerName] = useState('');
  const [managerEmail, setManagerEmail] = useState('');
  const [managerPhone, setManagerPhone] = useState('');
  const [startDate, setStartDate] = useState('');
  const [adminUserId, setAdminUserId] = useState('');
  const [isActive, setIsActive] = useState(true);

  const [geoPostcode, setGeoPostcode] = useState('');
  const [geoLat, setGeoLat] = useState('');
  const [geoLng, setGeoLng] = useState('');
  const [geoLookupLoading, setGeoLookupLoading] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const [branchesRes, usersRes] = await Promise.allSettled([
        graphqlRequest(GET_BRANCHES),
        graphqlRequest(GET_ALL_USERS),
      ]);
      if (branchesRes.status === 'fulfilled') {
        setBranches(
          Array.isArray(branchesRes.value?.getMyCompanyBranches)
            ? branchesRes.value.getMyCompanyBranches
            : [],
        );
      } else {
        setBranches([]);
      }

      if (usersRes.status === 'fulfilled') {
        setUsers(
          Array.isArray(usersRes.value?.getAllUser?.data)
            ? usersRes.value.getAllUser.data
            : [],
        );
      } else {
        setUsers([]);
      }
    } catch (e) {
      setBranches([]);
      setUsers([]);
      setError(e?.message || 'Unable to load branches.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, navigate]);

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    const n = String(name || '').trim();
    if (!n) return;
    setSaving(true);
    setError('');
    try {
      await graphqlRequest(CREATE_BRANCH, {
        input: {
          name: n,
          location: location.trim() || null,
          managerName: managerName.trim() || null,
          managerEmail: managerEmail.trim() || null,
          managerPhone: managerPhone.trim() || null,
          startDate: startDate.trim() || null,
          adminUserId: adminUserId || null,
        },
      });
      setName('');
      setLocation('');
      setManagerName('');
      setManagerEmail('');
      setManagerPhone('');
      setStartDate('');
      setAdminUserId('');
      setShowModal(false);
      await load();
    } catch (e2) {
      setError(e2?.message || 'Unable to create branch.');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (b) => {
    setError('');
    setEditBranch(b);
    setName(b?.name || '');
    setLocation(b?.location || '');
    setManagerName(b?.managerName || '');
    setManagerEmail(b?.managerEmail || '');
    setManagerPhone(b?.managerPhone || '');
    setStartDate(b?.startDate || '');
    setAdminUserId(b?.adminUserId || '');
    setIsActive(b?.isActive !== false);
  };

  const handleUpdate = async (e) => {
    e?.preventDefault?.();
    if (!editBranch?.id || saving) return;
    const n = String(name || '').trim();
    if (!n) return;
    setSaving(true);
    setError('');
    try {
      await graphqlRequest(UPDATE_BRANCH, {
        input: {
          branchId: editBranch.id,
          name: n,
          location: location.trim() || null,
          managerName: managerName.trim() || null,
          managerEmail: managerEmail.trim() || null,
          managerPhone: managerPhone.trim() || null,
          startDate: startDate.trim() || null,
          adminUserId: adminUserId || null,
          isActive: !!isActive,
        },
      });
      setEditBranch(null);
      await load();
    } catch (e2) {
      setError(e2?.message || 'Unable to update branch.');
    } finally {
      setSaving(false);
    }
  };

  const openGeo = (b) => {
    setError('');
    setGeoBranch(b);
    setGeoPostcode(String(b?.postcode || ''));
    setGeoLat(b?.latitude != null ? String(b.latitude) : '');
    setGeoLng(b?.longitude != null ? String(b.longitude) : '');
  };

  const lookupGeoByPostcode = async () => {
    const pc = String(geoPostcode || '').trim();
    if (!/^\d{4}$/.test(pc)) {
      setError('Postcode must be 4 digits (AU).');
      return;
    }
    setGeoLookupLoading(true);
    setError('');
    try {
      const res = await fetch(`https://api.zippopotam.us/au/${pc}`);
      if (!res.ok) throw new Error('Could not find coordinates for this postcode');
      const json = await res.json();
      const place = Array.isArray(json?.places) ? json.places[0] : null;
      const lat = place?.latitude != null ? Number(place.latitude) : NaN;
      const lng = place?.longitude != null ? Number(place.longitude) : NaN;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        throw new Error('Coordinates not available for this postcode');
      }
      setGeoLat(String(lat));
      setGeoLng(String(lng));
    } catch (e) {
      setError(e?.message || 'Geo lookup failed');
    } finally {
      setGeoLookupLoading(false);
    }
  };

  const handleSaveGeo = async (e) => {
    e?.preventDefault?.();
    if (!geoBranch?.id || saving) return;
    const pc = String(geoPostcode || '').trim();
    const lat = Number(geoLat);
    const lng = Number(geoLng);
    if (!/^\d{4}$/.test(pc)) {
      setError('Postcode must be 4 digits (AU).');
      return;
    }
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setError('Latitude/Longitude are required (use Auto-fill).');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await graphqlRequest(UPDATE_BRANCH_LOCATION, {
        input: { branchId: geoBranch.id, postcode: pc, latitude: lat, longitude: lng },
      });
      setGeoBranch(null);
      await load();
    } catch (e2) {
      setError(e2?.message || 'Unable to update geo.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete?.id || deleting) return;
    setDeleting(true);
    setError('');
    try {
      await graphqlRequest(DELETE_BRANCH, { branchId: confirmDelete.id });
      setConfirmDelete(null);
      await load();
    } catch (e) {
      setError(e?.message || 'Unable to delete branch.');
    } finally {
      setDeleting(false);
    }
  };

  const adminOptions = useMemo(() => {
    return (Array.isArray(users) ? users : [])
      .filter((u) => u?.isActive !== false)
      .filter((u) => {
        const rolesList = Array.isArray(u?.roles) ? u.roles : [];
        const keys = rolesList.map(roleKey);
        return keys.some((k) => k.includes('ADMIN'));
      })
      .map((u) => {
        const name = `${u?.firstName || ''} ${u?.lastName || ''}`.trim();
        return {
          id: u.id,
          label: name ? `${name} (${u?.email || 'no email'})` : u?.email || u.id,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [users]);

  const userLabelById = useMemo(() => {
    const map = new Map();
    (Array.isArray(users) ? users : []).forEach((u) => {
      if (!u?.id) return;
      const full = `${u?.firstName || ''} ${u?.lastName || ''}`.trim();
      const label = full || u?.email || u.id;
      map.set(u.id, label);
    });
    return map;
  }, [users]);

  const fmtDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString();
  };

  const summary = useMemo(() => {
    const list = Array.isArray(branches) ? branches : [];
    const totalBranches = list.length;
    const activeBranches = list.filter((b) => b?.isActive !== false).length;
    const totalRevenue = list.reduce((sum, b) => sum + (Number(b?.totalRevenue) || 0), 0);
    const totalWorkforce = list.reduce((sum, b) => sum + (Number(b?.workforceCount) || 0), 0);
    const openDeals = list.reduce((sum, b) => sum + (Number(b?.openDeals) || 0), 0);
    const slaCompliance = (() => {
      const vals = list.map((b) => Number(b?.slaCompliance)).filter((n) => Number.isFinite(n));
      if (vals.length === 0) return null;
      return Math.round(vals.reduce((a, c) => a + c, 0) / vals.length);
    })();
    return { totalBranches, activeBranches, totalRevenue, totalWorkforce, openDeals, slaCompliance };
  }, [branches]);

  const fmtMoney = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return '—';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(v);
    } catch {
      return `$${Math.round(v).toLocaleString()}`;
    }
  };

  return (
    <div className="bm-page">
      <div className="bm-head">
        <div>
          <h1>Branch Overview</h1>
          <p>
            Monitor and manage all branches. Keep resources/inventory separate; KPIs here are branch-level.
          </p>
        </div>
        <button type="button" className="bm-add-btn" onClick={() => setShowModal(true)}>
          + Add Branch
        </button>
      </div>

      <section className="bm-stats">
        <div className="bm-stat">
          <div className="bm-stat-icon bm-stat-icon--blue" aria-hidden="true">
            <FiGrid />
          </div>
          <div className="bm-stat-body">
            <div className="bm-stat-label">Total Branches</div>
            <div className="bm-stat-value">{summary.totalBranches}</div>
          </div>
        </div>
        <div className="bm-stat">
          <div className="bm-stat-icon bm-stat-icon--green" aria-hidden="true">
            <FiCheckCircle />
          </div>
          <div className="bm-stat-body">
            <div className="bm-stat-label">Active Branches</div>
            <div className="bm-stat-value">{summary.activeBranches}</div>
          </div>
        </div>
        <div className="bm-stat">
          <div className="bm-stat-icon bm-stat-icon--purple" aria-hidden="true">
            <FiDollarSign />
          </div>
          <div className="bm-stat-body">
            <div className="bm-stat-label">Total Revenue (All Branches)</div>
            <div className="bm-stat-value">{fmtMoney(summary.totalRevenue)}</div>
          </div>
        </div>
        <div className="bm-stat">
          <div className="bm-stat-icon bm-stat-icon--amber" aria-hidden="true">
            <FiUsers />
          </div>
          <div className="bm-stat-body">
            <div className="bm-stat-label">Total Workforce</div>
            <div className="bm-stat-value">{summary.totalWorkforce || 0}</div>
          </div>
        </div>
        <div className="bm-stat">
          <div className="bm-stat-icon bm-stat-icon--pink" aria-hidden="true">
            <FiBriefcase />
          </div>
          <div className="bm-stat-body">
            <div className="bm-stat-label">Open Deals Across Branches</div>
            <div className="bm-stat-value">{summary.openDeals || 0}</div>
          </div>
        </div>
        <div className="bm-stat">
          <div className="bm-stat-icon bm-stat-icon--teal" aria-hidden="true">
            <FiShield />
          </div>
          <div className="bm-stat-body">
            <div className="bm-stat-label">SLA Compliance Rate</div>
            <div className="bm-stat-value">
              {summary.slaCompliance == null ? '—' : `${summary.slaCompliance}%`}
            </div>
          </div>
        </div>
      </section>

      <section className="bm-card">
        <div className="bm-card-head">
          <h2>Branch Performance</h2>
          <button type="button" className="bm-refresh" onClick={load} disabled={loading}>
            Refresh
          </button>
        </div>

        {loading ? <p className="bm-muted">Loading branches…</p> : null}
        {!loading && branches.length === 0 ? <p className="bm-muted">No branches created yet.</p> : null}

        {!loading && branches.length > 0 ? (
          <div className="bm-table-wrap">
            <table className="bm-table">
              <thead>
                <tr>
                  <th>Branch Name</th>
                  <th>Location</th>
                  <th>Branch Superadmin</th>
                  <th>Active Projects</th>
                  <th>Revenue</th>
                  <th>SLA Status</th>
                  <th>Quality Score</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {branches.map((b) => (
                  <tr key={b.id}>
                    <td>{b.name}</td>
                    <td>{b.location || '—'}</td>
                    <td>{userLabelById.get(b.adminUserId) || '—'}</td>
                    <td>{Number(b?.activeProjects) || 0}</td>
                    <td>{fmtMoney(b?.totalRevenue || 0)}</td>
                    <td>
                      {b?.slaStatus ? (
                        <span
                          className={`bm-pill bm-pill--${
                            String(b.slaStatus).toLowerCase() === 'good'
                              ? 'good'
                              : String(b.slaStatus).toLowerCase() === 'warning'
                                ? 'warn'
                                : 'bad'
                          }`}
                        >
                          {b.slaStatus}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{b?.qualityScore == null ? '—' : `${b.qualityScore}%`}</td>
                    <td>
                      <span
                        className={`bm-pill bm-pill--${
                          b?.isActive === false ? 'inactive' : 'active'
                        }`}
                      >
                        {b?.isActive === false ? 'Inactive' : 'Active'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="bm-action"
                          onClick={() => setViewBranch(b)}
                        >
                          <FiEye aria-hidden="true" />
                          <span>View</span>
                        </button>
                        <button
                          type="button"
                          className="bm-action"
                          onClick={() => openGeo(b)}
                        >
                          <FiGrid aria-hidden="true" />
                          <span>Set geo</span>
                        </button>
                        <button
                          type="button"
                          className="bm-action"
                          onClick={() => openEdit(b)}
                        >
                          <FiEdit2 aria-hidden="true" />
                          <span>Edit</span>
                        </button>
                        <button
                          type="button"
                          className="bm-action bm-action--danger"
                          onClick={() => setConfirmDelete(b)}
                        >
                          <FiTrash2 aria-hidden="true" />
                          <span>Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {showModal ? (
        <div className="bm-modal-overlay" role="presentation" onMouseDown={() => setShowModal(false)}>
          <div className="bm-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <div className="bm-modal-head">
              <div>
                <h3>Add New Branch</h3>
                <p>Create a new branch. Assign an admin and manager details.</p>
              </div>
              <button type="button" className="bm-x" onClick={() => setShowModal(false)} aria-label="Close">
                ×
              </button>
            </div>

            <form className="bm-modal-form" onSubmit={handleCreate}>
              <label>
                Branch Name
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Downtown Branch" />
              </label>
              <label>
                Location
                <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="New York, NY" />
              </label>
              <label>
                Branch Manager
                <input value={managerName} onChange={(e) => setManagerName(e.target.value)} placeholder="John Smith" />
              </label>
              <label>
                Email
                <input value={managerEmail} onChange={(e) => setManagerEmail(e.target.value)} placeholder="manager@branch.com" />
              </label>
              <label>
                Phone Number
                <input value={managerPhone} onChange={(e) => setManagerPhone(e.target.value)} placeholder="+1 (555) 123-4567" />
              </label>
              <label>
                Start Date
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </label>
              <label>
                Branch Superadmin
                <select value={adminUserId} onChange={(e) => setAdminUserId(e.target.value)}>
                  <option value="">(Default: me)</option>
                  {adminOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>

              {error ? <div className="bm-error">{error}</div> : null}

              <div className="bm-modal-actions">
                <button type="button" className="bm-btn bm-btn--ghost" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="bm-btn" disabled={saving || !String(name || '').trim()}>
                  {saving ? 'Creating…' : 'Create Branch'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {viewBranch ? (
        <div className="bm-modal-overlay" role="presentation" onMouseDown={() => setViewBranch(null)}>
          <div className="bm-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <div className="bm-modal-head">
              <div>
                <h3>Branch details</h3>
                <p>All available information for this branch.</p>
              </div>
              <button type="button" className="bm-x" onClick={() => setViewBranch(null)} aria-label="Close">
                ×
              </button>
            </div>

            <div className="bm-modal-form" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800 }}>Branch</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>{viewBranch?.name || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800 }}>Location</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>{viewBranch?.location || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800 }}>Branch Superadmin</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>
                  {userLabelById.get(viewBranch?.adminUserId) || '—'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800 }}>Start date</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>{fmtDate(viewBranch?.startDate)}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800 }}>Manager</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>{viewBranch?.managerName || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800 }}>Manager email</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>{viewBranch?.managerEmail || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800 }}>Manager phone</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>{viewBranch?.managerPhone || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800 }}>Status</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>
                  {viewBranch?.isActive === false ? 'Inactive' : 'Active'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800 }}>Postcode</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>
                  {viewBranch?.postcode || '—'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800 }}>Coordinates</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>
                  {viewBranch?.latitude != null && viewBranch?.longitude != null
                    ? `${viewBranch.latitude}, ${viewBranch.longitude}`
                    : '—'}
                </div>
              </div>

              <div style={{ gridColumn: '1 / -1', height: 1, background: '#e5e7eb', margin: '6px 0' }} />

              <div>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800 }}>Revenue</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>{fmtMoney(viewBranch?.totalRevenue || 0)}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800 }}>Active projects</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>{Number(viewBranch?.activeProjects) || 0}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800 }}>Workforce count</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>{Number(viewBranch?.workforceCount) || 0}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800 }}>Open deals</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>{Number(viewBranch?.openDeals) || 0}</div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800 }}>SLA compliance</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>
                  {viewBranch?.slaCompliance == null ? '—' : `${viewBranch.slaCompliance}%`}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800 }}>Quality score</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>
                  {viewBranch?.qualityScore == null ? '—' : `${viewBranch.qualityScore}%`}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 800 }}>SLA status</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: '#111827' }}>{viewBranch?.slaStatus || '—'}</div>
              </div>
            </div>

            <div className="bm-modal-actions">
              <button type="button" className="bm-btn" onClick={() => setViewBranch(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editBranch ? (
        <div className="bm-modal-overlay" role="presentation" onMouseDown={() => setEditBranch(null)}>
          <div className="bm-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <div className="bm-modal-head">
              <div>
                <h3>Edit branch</h3>
                <p>Update branch details and assign Branch Super Admin.</p>
              </div>
              <button type="button" className="bm-x" onClick={() => setEditBranch(null)} aria-label="Close">
                ×
              </button>
            </div>

            <form className="bm-modal-form" onSubmit={handleUpdate}>
              <label>
                Branch Name
                <input value={name} onChange={(e) => setName(e.target.value)} />
              </label>
              <label>
                Location
                <input value={location} onChange={(e) => setLocation(e.target.value)} />
              </label>
              <label>
                Branch Manager
                <input value={managerName} onChange={(e) => setManagerName(e.target.value)} />
              </label>
              <label>
                Email
                <input value={managerEmail} onChange={(e) => setManagerEmail(e.target.value)} />
              </label>
              <label>
                Phone Number
                <input value={managerPhone} onChange={(e) => setManagerPhone(e.target.value)} />
              </label>
              <label>
                Start Date
                <input type="date" value={startDate || ''} onChange={(e) => setStartDate(e.target.value)} />
              </label>
              <label>
                Assign Branch Superadmin
                <select value={adminUserId} onChange={(e) => setAdminUserId(e.target.value)}>
                  <option value="">(Keep current)</option>
                  {adminOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Status
                <select value={isActive ? 'active' : 'inactive'} onChange={(e) => setIsActive(e.target.value === 'active')}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>

              {error ? <div className="bm-error">{error}</div> : null}

              <div className="bm-modal-actions">
                <button type="button" className="bm-btn bm-btn--ghost" onClick={() => setEditBranch(null)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="bm-btn" disabled={saving || !String(name || '').trim()}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {geoBranch ? (
        <div className="bm-modal-overlay" role="presentation" onMouseDown={() => setGeoBranch(null)}>
          <div className="bm-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <div className="bm-modal-head">
              <div>
                <h3>Set branch geo</h3>
                <p>Save postcode + coordinates for proximity and branch coverage features.</p>
              </div>
              <button type="button" className="bm-x" onClick={() => setGeoBranch(null)} aria-label="Close">
                ×
              </button>
            </div>

            <form className="bm-modal-form" onSubmit={handleSaveGeo}>
              <label>
                Postcode (AU)
                <input value={geoPostcode} onChange={(e) => setGeoPostcode(e.target.value)} placeholder="2000" />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label>
                  Latitude
                  <input value={geoLat} onChange={(e) => setGeoLat(e.target.value)} placeholder="-33.8688" />
                </label>
                <label>
                  Longitude
                  <input value={geoLng} onChange={(e) => setGeoLng(e.target.value)} placeholder="151.2093" />
                </label>
              </div>

              <button
                type="button"
                className="bm-btn bm-btn--ghost"
                onClick={lookupGeoByPostcode}
                disabled={geoLookupLoading}
                style={{ justifySelf: 'start' }}
              >
                {geoLookupLoading ? 'Looking up…' : 'Auto-fill from postcode'}
              </button>

              {error ? <div className="bm-error">{error}</div> : null}

              <div className="bm-modal-actions">
                <button type="button" className="bm-btn bm-btn--ghost" onClick={() => setGeoBranch(null)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="bm-btn" disabled={saving}>
                  {saving ? 'Saving…' : 'Save geo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {confirmDelete ? (
        <div className="bm-modal-overlay" role="presentation" onMouseDown={() => setConfirmDelete(null)}>
          <div className="bm-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <div className="bm-modal-head">
              <div>
                <h3>Delete branch</h3>
                <p>
                  This will permanently remove <strong>{confirmDelete?.name}</strong>.
                </p>
              </div>
              <button type="button" className="bm-x" onClick={() => setConfirmDelete(null)} aria-label="Close">
                ×
              </button>
            </div>

            {error ? <div className="bm-error" style={{ margin: '0 16px 12px' }}>{error}</div> : null}

            <div className="bm-modal-actions" style={{ padding: '0 16px 16px' }}>
              <button type="button" className="bm-btn bm-btn--ghost" onClick={() => setConfirmDelete(null)} disabled={deleting}>
                Cancel
              </button>
              <button type="button" className="bm-btn" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

